import React, { useState } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    Progress,
    Row,
    Col
} from 'reactstrap';
import { uploadSignedApplication } from '../../helpers/Espo/application_helper';
import { validateFileUpload } from '../../helpers/statusManager';

const SignedApplicationUploadModal = ({ 
    isOpen, 
    toggle, 
    applicationId, 
    applicantName, 
    applicationNumber,
    onUploadSuccess 
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [alert, setAlert] = useState({ visible: false, message: '', color: 'info' });

    console.log('SignedApplicationUploadModal received applicationId:', applicationId, 'applicantName:', applicantName, 'applicationNumber:', applicationNumber);

    // File validation constants
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
    const ALLOWED_TYPES = ['application/pdf'];

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        
        if (!file) {
            setSelectedFile(null);
            setAlert({ visible: false, message: '', color: 'info' });
            return;
        }

        // Validate file using status manager
        const validation = validateFileUpload(file, 'pdf');
        
        if (!validation.isValid) {
            setAlert({
                visible: true,
                message: `File validation failed: ${validation.errors.join(', ')}`,
                color: 'danger'
            });
            setSelectedFile(null);
            e.target.value = '';
            return;
        }

        setSelectedFile(file);
        setAlert({
            visible: true,
            message: `Selected: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)}MB)`,
            color: 'success'
        });
    };    const handleUpload = async () => {
        if (!selectedFile) {
            setAlert({
                visible: true,
                message: 'Please select a file to upload.',
                color: 'warning'
            });
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            console.log('Starting upload for applicationId:', applicationId, 'with file:', selectedFile.name);
            
            // Simulate progress for UI feedback
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 200);

            // Make API call using the helper function
            const result = await uploadSignedApplication(applicationId, selectedFile);

            clearInterval(progressInterval);
            setUploadProgress(100);

            setAlert({
                visible: true,
                message: 'Signed application uploaded successfully!',
                color: 'success'
            });

            // Reset form
            setTimeout(() => {
                setSelectedFile(null);
                setUploadProgress(0);
                setUploading(false);
                document.getElementById('signedFormFile').value = '';
                
                if (onUploadSuccess) {
                    onUploadSuccess(result);
                }
                
                setTimeout(() => {
                    toggle();
                    setAlert({ visible: false, message: '', color: 'info' });
                }, 1500);
            }, 2000);

        } catch (error) {
            console.error('Upload error:', error);
            setAlert({
                visible: true,
                message: `Upload failed: ${error.message}`,
                color: 'danger'
            });
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleModalClose = () => {
        if (!uploading) {
            setSelectedFile(null);
            setAlert({ visible: false, message: '', color: 'info' });
            setUploadProgress(0);
            document.getElementById('signedFormFile').value = '';
            toggle();
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Modal 
            isOpen={isOpen} 
            toggle={handleModalClose} 
            size="lg" 
            backdrop={uploading ? "static" : true}
            centered={true}
        >
            <ModalHeader toggle={!uploading ? handleModalClose : undefined}>
                <i className="fas fa-upload me-2"></i>
                Upload Signed PDF Application
            </ModalHeader>
            
            <ModalBody>
                <Row>
                    <Col md={12}>
                        <div className="mb-3">
                            <h6 className="text-primary">
                                <i className="fas fa-user me-2"></i>
                                Applicant: {applicantName}
                            </h6>
                            <small className="text-muted">Application #: {applicationNumber || applicationId}</small>
                        </div>

                        {alert.visible && (
                            <Alert color={alert.color} className="mb-3">
                                {alert.message}
                            </Alert>
                        )}

                        <Form>
                            <FormGroup>
                                <Label for="signedFormFile" className="fw-bold">
                                    <i className="fas fa-file-pdf me-2"></i>
                                    Select Signed PDF Application
                                </Label>
                                <Input
                                    type="file"
                                    name="signedFormFile"
                                    id="signedFormFile"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    disabled={uploading}
                                />
                                <div className="form-text">
                                    <strong>Requirements:</strong>
                                    <ul className="mb-0 mt-1">
                                        <li>File format: PDF only</li>
                                        <li>Maximum size: 10MB</li>
                                        <li>Must contain your signature and community reference signatures</li>
                                    </ul>
                                </div>
                            </FormGroup>

                            {uploading && (
                                <FormGroup>
                                    <Label className="fw-bold">Upload Progress</Label>
                                    <Progress
                                        value={uploadProgress}
                                        color={uploadProgress === 100 ? "success" : "primary"}
                                        striped={uploadProgress < 100}
                                        animated={uploadProgress < 100}
                                    >
                                        {uploadProgress}%
                                    </Progress>
                                </FormGroup>
                            )}

                            {selectedFile && !uploading && (
                                <div className="border rounded p-3 bg-light">
                                    <h6 className="text-success mb-2">
                                        <i className="fas fa-check-circle me-2"></i>
                                        File Ready for Upload
                                    </h6>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div><strong>Name:</strong> {selectedFile.name}</div>
                                            <div><strong>Size:</strong> {formatFileSize(selectedFile.size)}</div>
                                            <div><strong>Type:</strong> {selectedFile.type}</div>
                                        </div>
                                        <div className="text-end">
                                            <i className="fas fa-file-pdf fa-3x text-danger"></i>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Form>
                    </Col>
                </Row>
            </ModalBody>
            
            <ModalFooter>
                <Button
                    color="secondary"
                    onClick={handleModalClose}
                    disabled={uploading}
                >
                    <i className="fas fa-times me-1"></i>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                >
                    {uploading ? (
                        <>
                            <i className="fas fa-spinner fa-spin me-1"></i>
                            Uploading...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-upload me-1"></i>
                            Upload Signed PDF
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default SignedApplicationUploadModal;
